import React, { useState, useRef, useCallback } from 'react';
import { uploadRider, getMyRiders, getProfileRiders, deleteRider, downloadRider } from '../lib/api';
import { FileText, Upload, X, Download, Trash2, File, Image } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const TYPE_ICONS = {
  'application/pdf': FileText,
  'image/png': Image,
  'image/jpeg': Image,
  'image/jpg': Image,
  'image/webp': Image,
};

const RIDER_TYPE_LABELS = {
  technical: 'Technical rider',
  hospitality: 'Hospitality rider',
  stage_plot: 'Stage plot',
};

function formatRiderType(riderType) {
  return RIDER_TYPE_LABELS[riderType] || 'Rider';
}

function stripPdfExtension(name) {
  return typeof name === 'string' ? name.replace(/\.pdf$/i, '') : name;
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatUploadedDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) {
    const w = Math.floor(diffDays / 7);
    return w === 1 ? '1 week ago' : `${w} weeks ago`;
  }
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function RiderUpload({ profileId, editable = false }) {
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  React.useEffect(() => {
    loadRiders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, editable]);

  const loadRiders = async () => {
    try {
      const res = editable
        ? await getMyRiders()
        : await getProfileRiders(profileId);
      setRiders(res.data);
    } catch {
      setRiders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    const maxSize = 10 * 1024 * 1024;

    setUploading(true);
    let successCount = 0;

    for (const file of Array.from(files)) {
      if (!allowed.includes(file.type)) {
        toast.error(`${file.name}: Only PDF and image files allowed`);
        continue;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name}: File too large (max 10MB)`);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('label', file.name.replace(/\.[^/.]+$/, ''));
        formData.append('riderType', 'technical');
        await uploadRider(formData);
        successCount++;
      } catch (err) {
        toast.error(`${file.name}: Upload failed`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} rider${successCount > 1 ? 's' : ''} uploaded`);
      loadRiders();
    }
    setUploading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (riderId, label) => {
    try {
      await deleteRider(riderId);
      setRiders(prev => prev.filter(r => r.id !== riderId));
      toast.success(`"${label}" removed`);
    } catch {
      toast.error('Failed to delete');
    }
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  }, [handleUpload]);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  if (loading) {
    return (
      <div className="py-4 text-center text-xs" style={{ color: 'var(--t3)', fontFamily: 'var(--font-mono)' }}>
        Loading riders...
      </div>
    );
  }

  // Read-only view for public profiles
  if (!editable) {
    if (riders.length === 0) return null;
    return (
      <RiderListWithPreview riders={riders} />
    );
  }

  // Editable view (owner)
  return (
    <div data-testid="riders-upload-section">
      {/* Drop zone */}
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={() => !uploading && fileRef.current?.click()}
        className={cn(
          'relative rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-all duration-200',
          dragOver ? 'border-[var(--amber)] bg-[rgba(200,146,62,0.06)]' : 'border-[var(--b2)] hover:border-[var(--t3)] hover:bg-[rgba(255,255,255,0.02)]',
          uploading && 'opacity-60 pointer-events-none',
        )}
        data-testid="rider-drop-zone"
      >
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp"
          multiple
          className="hidden"
          onChange={(e) => { handleUpload(e.target.files); e.target.value = ''; }}
          data-testid="rider-file-input"
        />
        <div className="flex flex-col items-center gap-2">
          <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ background: dragOver ? 'rgba(200,146,62,0.15)' : 'var(--bg-card-inner)' }}>
            <Upload className="h-5 w-5" style={{ color: dragOver ? 'var(--amber)' : 'var(--t2)' }} />
          </div>
          <div>
            <p className="text-sm font-medium" style={{ color: 'var(--t1)' }}>
              {uploading ? 'Uploading...' : dragOver ? 'Drop files here' : 'Drop riders here or click to browse'}
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--t3)', fontFamily: 'var(--font-mono)' }}>
              PDF, PNG, JPG · Max 10MB per file
            </p>
          </div>
        </div>
      </div>

      {/* Uploaded files list */}
      {riders.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="riders-list">
          {riders.map((rider) => {
            const Icon = TYPE_ICONS[rider.contentType] || File;
            return (
              <div
                key={rider.id}
                className="flex items-center gap-3 p-3 rounded-lg group transition-all"
                style={{ background: 'var(--bg-card-inner)', border: '1px solid var(--b1-solid)' }}
                data-testid={`rider-item-${rider.id}`}
              >
                <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(200,146,62,0.1)' }}>
                  <Icon className="h-4 w-4" style={{ color: 'var(--amber)' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" style={{ color: 'var(--t0)' }}>{rider.label}</div>
                  <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--t3)', fontFamily: 'var(--font-mono)' }}>
                    <span>{rider.riderType === 'technical' ? 'Technical' : 'Hospitality'}</span>
                    <span>·</span>
                    <span>{formatFileSize(rider.fileSize)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <a
                    href={downloadRider(rider.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded-md transition-colors hover:bg-[rgba(255,255,255,0.06)]"
                    title="Download"
                    data-testid={`rider-download-btn-${rider.id}`}
                  >
                    <Download className="h-3.5 w-3.5" style={{ color: 'var(--t2)' }} />
                  </a>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(rider.id, rider.label); }}
                    className="p-1.5 rounded-md transition-colors hover:bg-[rgba(212,84,58,0.1)]"
                    title="Delete"
                    data-testid={`rider-delete-btn-${rider.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" style={{ color: 'var(--red-flag)' }} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Rider list (download-only, tap-anywhere row) ───────────────── */
function RiderListWithPreview({ riders }) {
  return (
    <div data-testid="riders-public-list">
      <div className="space-y-2">
        {riders.map((rider) => {
          const Icon = TYPE_ICONS[rider.contentType] || File;
          const displayName = stripPdfExtension(rider.label);
          return (
            <a
              key={rider.id}
              href={downloadRider(rider.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-lg transition-colors hover:bg-[rgba(255,255,255,0.03)]"
              style={{ background: 'var(--bg-card-inner)', border: '1px solid var(--b1-solid)', textDecoration: 'none' }}
              data-testid={`rider-item-${rider.id}`}
            >
              <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(200,146,62,0.1)' }}>
                <Icon className="h-4 w-4" style={{ color: 'var(--amber)' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium" style={{ color: 'var(--t0)', wordBreak: 'break-word' }}>
                  {displayName}
                </div>
                <div className="text-[11px] mt-0.5" style={{ color: 'var(--t3)', fontFamily: 'var(--font-mono)' }}>
                  {formatRiderType(rider.riderType)} · {formatFileSize(rider.fileSize)}
                  {formatUploadedDate(rider.createdAt) ? ` · Uploaded ${formatUploadedDate(rider.createdAt)}` : ''}
                </div>
              </div>
              <Download className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--t2)' }} aria-hidden />
            </a>
          );
        })}
      </div>
    </div>
  );
}

export default RiderUpload;
